/**
 * The one name of a rate card (CU-869fh646w, UX decision D1).
 *
 * A rate card has no name of its own: `RepricingRateCardFace` carries a
 * site and three axes and nothing else. What the operator recognises as
 * "the rate card of Sunny Cars" is the label the four axes compose —
 * `Site · Tariff type · Insurance · Franchise`, by name, never an id
 * (UX obligatory 1, CU-869ffwj71). Every screen names a rate card
 * through this function so the name is the same in Rate cards, in the
 * Strategies table, in the wizard and in the Activity.
 *
 * Two forms. The **elided** one leaves the blank axes out — `"Sunny
 * Cars"` for a card with everything blank, `"DoYouSpain · Prepaid ·
 * Zero excess"` for a pinned one — because since this task a blank axis
 * is a choice ("any") and a `—` in its slot reads as data that is
 * missing. Two cards of one site still differ on some pinned axis (the
 * tuple is unique), so the elided name stays unique. The **full** one
 * names every axis, with `All tariff types` / `All insurance` / `All
 * franchises` on the blank ones, and goes in the `title`.
 *
 * Pure: the catalogues are passed in (`siteCatalog.labelOf`,
 * `axes.prepaidName` / `insuranceName` / `franchiseName`), so the
 * sentence is tested without mounting anything and no insurance name
 * is ever a literal (`copy.md`).
 */

export const ALL_LABEL = {
	site:      'All sites',
	tariff:    'All tariff types',
	insurance: 'All insurance',
	franchise: 'All franchises',
}

const present = (v) => v !== null && v !== undefined && v !== ''

/**
 * `faceLabel(face, siteCatalog, axes, { elide, franchise })`
 *
 * - `elide: true` leaves the blank axes out; `false` (default) names them
 *   with the "All …" labels.
 * - `franchise: false` leaves the franchise axis out altogether — the
 *   Activity names a decision's rate card by site, tariff type and
 *   insurance, which are the axes a decision is about.
 * - `siteCatalog` may be `null` when the caller has no site catalogue;
 *   the site is then left out rather than painted as an id.
 */
export function faceLabel(face, siteCatalog, axes, { elide = false, franchise = true } = {}) {
	if (!face) return null
	const parts = []
	if (siteCatalog) {
		parts.push(present(face.site_id) ? siteCatalog.labelOf(face.site_id) : (elide ? null : ALL_LABEL.site))
	}
	parts.push(present(face.is_prepaid)
		? axes.prepaidName(Boolean(Number(face.is_prepaid)))
		: (elide ? null : ALL_LABEL.tariff))
	parts.push(present(face.ps_insurance_id)
		? axes.insuranceName(face.ps_insurance_id)
		: (elide ? null : ALL_LABEL.insurance))
	if (franchise) {
		parts.push(present(face.ps_provider_id)
			? axes.franchiseName(face.ps_provider_id)
			: (elide ? null : ALL_LABEL.franchise))
	}
	return parts.filter(Boolean).join(' · ')
}

/** The four axes, blank ones said as "All …" — what goes in a `title`. */
export const faceLabelFull = (face, siteCatalog, axes, opts = {}) =>
	faceLabel(face, siteCatalog, axes, { ...opts, elide: false })
